/**
 * Manage an interview's panel — `POST /recruiting/interviews/{id}/panel`, WP-W4.
 *
 * ── The panel is observable only through a mutate ────────────────────────────
 *
 * `openapi.yaml` gives the panel **no GET**: `RecruitingInterview` carries no
 * panelists, and the only endpoint that returns the panel is the add/remove mutate,
 * which answers with the panel as it stands afterwards. So this section is honest about
 * that — before any change it shows a note, and after a change it lists the full
 * membership the server returned, not just the row that moved. `mine=true` on the list
 * is the caller's own answer to "am I on it"; this panel is the coordinator's tool to
 * seat and unseat others.
 *
 * Managing the panel needs `recruiting.manage_candidates`; a re-add of an already-seated
 * interviewer answers `409`, and a caller without the permission is refused `403` — both
 * rendered through `ApiErrorNote`.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { PanelRole, RecruitingPanelMember } from '../../api/recruiting';
import { PANEL_ROLES } from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import { PANEL_ROLE_LABEL } from '../../recruiting/interview';
import { ApiErrorNote } from '../../states/ApiErrorNote';

export interface InterviewPanelPanelProps {
  /** The panel as the last mutate returned it, or `null` before any change this session. */
  readonly panel: readonly RecruitingPanelMember[] | null;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onAdd: (employeeId: string, role: PanelRole) => void;
  readonly onRemove: (panelistId: string) => void;
}

export function InterviewPanelPanel({
  panel,
  pending,
  error,
  onAdd,
  onRemove,
}: InterviewPanelPanelProps): ReactNode {
  const { t } = useI18n();
  const [employeeId, setEmployeeId] = useState('');
  const [role, setRole] = useState<PanelRole>('panelist');

  const ready = employeeId.trim().length > 0;

  return (
    <section
      className="wp-section__subsection"
      aria-labelledby="w4-panel-heading"
      data-testid="w4-panel"
    >
      <h3 className="wp-section__title" id="w4-panel-heading">
        {t('recruiting.interviews.panel.title')}
      </h3>
      <p className="wp-small wp-muted">{t('recruiting.interviews.panel.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || pending) return;
          onAdd(employeeId.trim(), role);
          setEmployeeId('');
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-panel-add-id">
            {t('recruiting.interviews.panel.employeeId')}
          </label>
          <input
            className="wp-field__input"
            id="w4-panel-add-id"
            data-testid="w4-panel-add-id"
            type="text"
            value={employeeId}
            autoComplete="off"
            onChange={(event) => {
              setEmployeeId(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('recruiting.interviews.panel.employeeIdHint')}</p>
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-panel-add-role">
            {t('recruiting.interviews.panel.role')}
          </label>
          <select
            className="wp-field__input"
            id="w4-panel-add-role"
            data-testid="w4-panel-add-role"
            value={role}
            onChange={(event) => {
              setRole(event.target.value as PanelRole);
            }}
          >
            {PANEL_ROLES.map((value) => (
              <option key={value} value={value}>
                {t(PANEL_ROLE_LABEL[value])}
              </option>
            ))}
          </select>
        </div>
        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w4-panel-add-submit"
            disabled={!ready || pending}
          >
            {pending
              ? t('recruiting.interviews.panel.adding')
              : t('recruiting.interviews.panel.add')}
          </button>
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('recruiting.interviews.panel.subject')}
          testId="w4-panel-error"
        />
      )}

      {panel === null ? (
        <p className="wp-small wp-muted" data-testid="w4-panel-unknown">
          {t('recruiting.interviews.panel.unknown')}
        </p>
      ) : panel.length === 0 ? (
        <p className="wp-small wp-muted" data-testid="w4-panel-empty">
          {t('recruiting.interviews.panel.empty')}
        </p>
      ) : (
        <ul className="wp-rows" data-testid="w4-panel-list">
          {panel.map((member) => (
            <li className="wp-row" key={member.id} data-testid={`w4-panel-member-${member.id}`}>
              <div className="wp-row__main">
                <p>{member.interviewer_employee_id}</p>
                <p className="wp-small wp-muted">
                  {member.panel_role === null
                    ? t('recruiting.interviews.panel.roleNone')
                    : t(PANEL_ROLE_LABEL[member.panel_role])}
                </p>
              </div>
              <div className="wp-row__figure">
                <button
                  className="wp-button wp-button--danger"
                  type="button"
                  data-testid={`w4-panel-remove-${member.id}`}
                  disabled={pending}
                  onClick={() => {
                    onRemove(member.id);
                  }}
                >
                  {t('recruiting.interviews.panel.remove')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
